import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/config";
import { readRaw, writeRaw, COUNTRY_KEY } from "@/lib/storage";

const CountryContext = createContext();

const DEFAULT_COUNTRY = { code: "IN", name: "India", flag: "🇮🇳" };

const readStored = () => {
  try {
    // Goes through lib/storage so the native build reads device storage rather
    // than the web view's localStorage, which is not durable there.
    const raw = readRaw(COUNTRY_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.code ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Guess the visitor's region from their browser locale.
 *
 * Locale is already in the browser, so this needs no geo-IP service and no network
 * call. Only used when nothing has been stored yet - an explicit choice always wins.
 */
const detectRegionCode = () => {
  const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const locale of locales) {
    const region = locale?.split("-")[1]?.toUpperCase();
    if (region) return region;
  }
  return null;
};

/**
 * The region to start from, decided before the first render.
 *
 * This used to wait for /countries to come back, which meant every visitor
 * outside India fired one request for India and a second for their own region -
 * double the YouTube quota, and whichever response landed last decided what the
 * page showed. The locale is available synchronously, so use it, and fill in the
 * proper name and flag once the country list arrives.
 */
const initialCountry = () => {
  const stored = readStored();
  if (stored) return stored;

  const region = detectRegionCode();
  return region ? { code: region, name: region, flag: "", provisional: true } : DEFAULT_COUNTRY;
};

export const CountryProvider = ({ children }) => {
  // Start from the stored choice, else the browser locale - so the first render is
  // already the region we are going to keep.
  const [country, setCountryState] = useState(initialCountry);
  const [countries, setCountries] = useState([]);
  const [autoDetected, setAutoDetected] = useState(false);

  const setCountry = useCallback((next) => {
    setCountryState(next);
    writeRaw(COUNTRY_KEY, JSON.stringify(next));
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${API}/countries`);
        setCountries(res.data);

        if (!readStored()) {
          // Upgrade the locale guess to the real record (name + flag), or fall
          // back to the default if the region is not one we support.
          const match = res.data.find((c) => c.code === country.code);
          setCountryState(match || DEFAULT_COUNTRY);
          setAutoDetected(Boolean(match) && match.code !== DEFAULT_COUNTRY.code);
        }
      } catch (e) {
        console.error("Failed to fetch countries:", e);
      }
    };
    fetchCountries();
    // Runs once: re-running on country change would fight the user's selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CountryContext.Provider value={{ country, setCountry, countries, autoDetected }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => useContext(CountryContext);
